/*
 * Landing site (RED-398): the only JavaScript on the Blade landing pages (resources/views/web/**).
 * Vanilla and import-free. It is NOT part of the Vue app: never import it from app.js, and never
 * load app.js on a landing page.
 *
 * Every class this file relies on is written in the Blade markup (web.css only scans the views),
 * so state lives in attributes: `hidden`, `aria-expanded`, `data-reveal`.
 */

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

function isVisible(element) {
  return element.getClientRects().length > 0;
}

/* Scroll lock shared by the mobile menu and the dialogs. Counted, so one closing never unlocks
   the page while another is still open; the scrollbar width is padded so nothing shifts. */
let scrollLocks = 0;

function lockScroll() {
  scrollLocks += 1;
  if (scrollLocks > 1) {
    return;
  }
  const root = document.documentElement;
  const scrollbar = window.innerWidth - root.clientWidth;
  root.style.overflow = 'hidden';
  if (scrollbar > 0) {
    root.style.paddingRight = `${scrollbar}px`;
  }
}

function unlockScroll() {
  if (scrollLocks === 0) {
    return;
  }
  scrollLocks -= 1;
  if (scrollLocks === 0) {
    document.documentElement.style.overflow = '';
    document.documentElement.style.paddingRight = '';
  }
}

/* Desktop dropdowns: one open at a time; Esc closes and returns focus to its button; a click
   outside, a pick, or tabbing out closes. */
function initDropdowns() {
  const nav = document.querySelector('[data-dropdowns]');
  if (!nav) {
    return;
  }
  const buttons = [...nav.querySelectorAll('[data-dropdown-button]')];
  const panelOf = (button) => document.getElementById(button.getAttribute('aria-controls'));
  const isOpen = (button) => button.getAttribute('aria-expanded') === 'true';

  function close(button) {
    button.setAttribute('aria-expanded', 'false');
    panelOf(button).hidden = true;
  }

  function closeAll() {
    buttons.filter(isOpen).forEach(close);
  }

  function open(button) {
    closeAll();
    button.setAttribute('aria-expanded', 'true');
    panelOf(button).hidden = false;
  }

  buttons.forEach((button) => {
    button.addEventListener('click', () => (isOpen(button) ? close(button) : open(button)));
  });

  nav.addEventListener('click', (event) => {
    if (event.target.closest('a')) {
      closeAll();
    }
  });

  nav.addEventListener('focusout', (event) => {
    if (!nav.contains(event.relatedTarget)) {
      closeAll();
    }
  });

  document.addEventListener('pointerdown', (event) => {
    if (!nav.contains(event.target)) {
      closeAll();
    }
  });

  document.addEventListener('keydown', (event) => {
    const openButton = buttons.find(isOpen);
    if (event.key !== 'Escape' || !openButton) {
      return;
    }
    close(openButton);
    openButton.focus();
  });
}

/* Mobile menu: focus trapped between the toggle and the menu, page scroll locked, Esc closes and
   returns focus to the toggle (RED-397 QA bug 8). */
function initMobileMenu() {
  const button = document.querySelector('[data-mobile-menu-button]');
  const panel = button && document.getElementById(button.getAttribute('aria-controls'));
  if (!panel) {
    return;
  }
  const desktop = window.matchMedia('(min-width: 64rem)');
  let open = false;

  function setOpen(next, { returnFocus = false } = {}) {
    if (next === open) {
      return;
    }
    open = next;
    button.setAttribute('aria-expanded', String(next));
    button.setAttribute('aria-label', next ? button.dataset.labelClose : button.dataset.labelOpen);
    panel.hidden = !next;
    if (next) {
      lockScroll();
    } else {
      unlockScroll();
      if (returnFocus) {
        button.focus();
      }
    }
  }

  button.addEventListener('click', () => setOpen(!open));

  // A pick closes the menu before the browser follows the link, so an anchor on this page
  // scrolls with the lock already released.
  panel.addEventListener('click', (event) => {
    if (event.target.closest('a')) {
      setOpen(false);
    }
  });

  desktop.addEventListener('change', (event) => {
    if (event.matches) {
      setOpen(false);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!open) {
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false, { returnFocus: true });
      return;
    }
    if (event.key !== 'Tab') {
      return;
    }
    const items = [button, ...panel.querySelectorAll(FOCUSABLE)].filter(isVisible);
    const first = items[0];
    const last = items[items.length - 1];
    const current = document.activeElement;
    if (!items.includes(current)) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && current === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && current === last) {
      event.preventDefault();
      first.focus();
    }
  });
}

/* "Read more" dialogs: native <dialog>. showModal() traps focus and closes on Esc; we add the
   backdrop click, the scroll lock (RED-397 QA bug 9) and the focus return to the opener. */
function initDialogs() {
  document.querySelectorAll('[data-dialog-open]').forEach((opener) => {
    const dialog = document.getElementById(opener.dataset.dialogOpen);
    if (!dialog || typeof dialog.showModal !== 'function') {
      return;
    }
    opener.addEventListener('click', () => {
      if (dialog.open) {
        return;
      }
      dialog.showModal();
      lockScroll();
    });
    dialog.addEventListener('close', () => {
      unlockScroll();
      opener.focus();
    });
    // A click on the backdrop lands on the <dialog> itself, not on its content.
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) {
        dialog.close();
      }
    });
    dialog.querySelectorAll('[data-dialog-close]').forEach((closer) => {
      closer.addEventListener('click', () => dialog.close());
    });
  });
}

/* Plays the "Motion" reveal animations in web.css once, when an item scrolls into view. Only items
   below the fold are hidden, so everything is visible without JS and on first paint. With reduced
   motion nothing is hidden at all. */
function initReveal() {
  if (reducedMotion.matches || !('IntersectionObserver' in window)) {
    return;
  }
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.setAttribute('data-reveal', 'in');
          observer.unobserve(entry.target);
        }
      });
    },
    { rootMargin: '0px 0px -15% 0px' },
  );
  document.querySelectorAll('.reveal, .reveal-group > *, .chart-draw').forEach((item) => {
    if (item.getBoundingClientRect().top < window.innerHeight) {
      return;
    }
    item.setAttribute('data-reveal', 'hidden');
    observer.observe(item);
  });
}

/* Contact form: one submit at a time; after a round trip, focus lands on the first invalid field
   or on the thank-you message instead of the top of the page. */
function initContactForm() {
  const form = document.querySelector('[data-contact-form]');
  const submit = form?.querySelector('[type="submit"]');
  const label = submit?.querySelector('[data-submit-label]');
  if (submit && label) {
    const idleLabel = label.textContent;
    form.addEventListener('submit', () => {
      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
      label.textContent = submit.dataset.pendingLabel || idleLabel;
    });
    // A page restored from the back/forward cache would keep the disabled button otherwise.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        submit.disabled = false;
        submit.removeAttribute('aria-busy');
        label.textContent = idleLabel;
      }
    });
  }
  const target =
    document.querySelector('[data-contact-form] [aria-invalid="true"]') ??
    document.querySelector('[data-contact-focus]');
  if (!target) {
    return;
  }
  // The visitor was at the form when they sent it, so the reloaded page puts them back there at
  // once, with the field (or the message) centred and focused.
  //
  // The server sends them to /#kontakt, the section top, which on a phone leaves the field below
  // the fold. With the html element's smooth scrolling on, Chrome plays that fragment scroll
  // after `load`, after our own scroll, and wins. So smooth scrolling is held off until we are
  // done: the browser then jumps to #kontakt while the page loads, and we centre the target last.
  const root = document.documentElement;
  root.style.scrollBehavior = 'auto';
  const showTarget = () => {
    target.scrollIntoView({ block: 'center' });
    target.focus({ preventScroll: true });
  };
  showTarget();
  // Repeated once loaded: the fragment jump clears focus (the section is not focusable), and
  // images and fonts above the form may have moved it.
  const settle = () =>
    requestAnimationFrame(() => {
      showTarget();
      root.style.scrollBehavior = '';
    });
  if (document.readyState === 'complete') {
    settle();
  } else {
    window.addEventListener('load', settle, { once: true });
  }
}

initDropdowns();
initMobileMenu();
initDialogs();
initReveal();
initContactForm();
